import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo } from "apollo-angular";
import dayjs from "dayjs";
import { Subscription } from "rxjs";

import { KeyValuePipe, formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { Category, Impact, NotificationFilter, ProcessState } from "../../../apis/networkdashboard/graphql/graphql";
import { CimResult } from "../../../apis/networkdashboard/static/custom-types";
import { CIM_QUERY } from "../../../apis/networkdashboard/static/graphql-queries/cim-notifications";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { WebarchiveCalendar } from "../webarchive-calendar/webarchive-calendar";

@Component({
  selector: "webarchive",
  templateUrl: "webarchive.html",
  styleUrl: "webarchive.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, KeyValuePipe, TranslatePipe, WebarchiveCalendar],
})
export class Webarchive {
  private msgsrv = inject(MessageService);
  private auth = inject(AuthService);
  private apollo = inject(Apollo);
  private destroyRef = inject(DestroyRef);

  readonly filterSubscriptionName = input.required<string>();
  readonly filterSubscriptionId = input.required<string>();

  protected webarchiveData: Map<number, { [key: string]: any }> = new Map<number, { [key: string]: any }>();
  private calendarActive = signal(false);
  protected activeYear = signal<number | null>(null);
  protected loading = signal(false);
  private apolloQuery?: Subscription;

  constructor() {
    this.msgsrv.malfunctionFilterDate$.subscribe(() => {
      // hide the dates after a date is selected
      this.activeYear.set(null);
    });

    effect(() => {
      this.filterSubscriptionId();
      this.addDefaultValues();
      this.fetchMessages();
    });
  }

  protected toggleCalendar(year: number) {
    this.calendarActive.set(false);
    this.activeYear.set(null);

    if (year !== this.activeYear()) {
      setTimeout(() => {
        this.calendarActive.set(true);
        this.activeYear.set(year);
      }, 100);
    }
  }

  private fetchMessages() {
    this.loading.set(true);
    this.apolloQuery?.unsubscribe();

    const customerId = this.auth.viewingCustomer().customerId;
    const filter: NotificationFilter = {
      customerId,
      accessCustomerId: customerId,
      subscriptionIds: [this.filterSubscriptionId()],
      impacts: [Impact.Down, Impact.ReducedRedundancy, Impact.ResilienceLoss, Impact.Unknown],
      statuses: [ProcessState.Open, ProcessState.Updated, ProcessState.Closed],
    };

    this.apolloQuery = this.apollo
      .watchQuery<CimResult>({
        query: CIM_QUERY,
        variables: { filter },
      })
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ data }) => {
        if (data) {
          this.aggregateData(data as CimResult);
        }
        this.loading.set(false);
      });
  }

  private aggregateData(messagesData: CimResult) {
    messagesData.notifications.forEach((message) => {
      const messageTime = dayjs(message.startDate).toDate();
      const year = messageTime.getFullYear();
      if (!this.webarchiveData.has(year)) {
        this.addDefaultValueyear(year);
      }
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      this.webarchiveData.get(year).get("tickets")[messageTime.getMonth()]++;

      const dateString = formatDate(messageTime, "yyyy-MM-dd", "en-US");
      if (message.category === Category.Plannedwork) {
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        this.webarchiveData.get(year).get("maintenance").push(dateString);
      } else if (message.category === Category.Malfunction) {
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        this.webarchiveData.get(year).get("malfunction").push(dateString);
      }
    });
  }

  private addDefaultValues() {
    this.webarchiveData = new Map<number, { [key: string]: any }>();
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.addDefaultValueyear(currentYear - i);
    }
  }

  private addDefaultValueyear(year: number) {
    this.webarchiveData.set(year, new Map<number, { [key: string]: any }>());
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    this.webarchiveData.get(year).set("tickets", [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    this.webarchiveData.get(year).set("malfunction", []);
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    this.webarchiveData.get(year).set("maintenance", []);
  }

  protected ticketsAreZero(year: number, tickets: number[]) {
    if (year === new Date().getFullYear()) {
      return false;
    }

    return !tickets || tickets.every((ticket) => ticket === 0);
  }
}
